import { Timestamp } from "firebase/firestore";
import { v4 } from "uuid";
import { lang } from "../i18nLang";

export function uuid() {
  return v4();
}

export function uuidFirebase() {
  return v4().replace(/\-/g, "").slice(0, 20);
}

export function inputTextToNumber(value: string): number {
  if (!value) return 0;
  value = value.replaceAll(".", "").replace(",", ".");
  return Number(value);
}

export function inputTextToDate(value: string): Date | undefined {
  if (!value) return;

  let parts = value.split("/");
  if (parts.length !== 3) return;

  const day = Number(parts[0]);
  const month = Number(parts[1]);
  const year = Number(parts[2]);

  if (year < 1900) {
    console.error("Data inválida!", `${month}-${day}-${year}`);
    return;
  }

  return new Date(`${month}-${day}-${year}`);
}

export function isEmptyObject(obj: any): boolean {
  if (obj === undefined || obj === null) return true;
  if (!isObject(obj)) return false;

  return !obj || !Object.keys(obj).length;
}

export function isObject(obj: any): boolean {
  if (obj === undefined || obj === null) return false;
  if (typeof obj !== "object") return false;
  if (Array.isArray(obj)) return false;
  if (obj instanceof Date || obj instanceof Timestamp) return false;
  return true;
}

export function isDate(value: any): boolean {
  return (
    Object.prototype.toString.call(value) === "[object Date]" &&
    value.toString() !== "Invalid Date"
  );
}

export function isNumber(value: any) {
  try {
    const numberValue = Number(value);
    return !isNaN(numberValue) && numberValue === value;
  } catch (error) {}

  return false;
}

export function toNumber(value: any): number {
  try {
    const numberValue = Number(value);
    if (!isNaN(numberValue) && numberValue == value) {
      return numberValue;
    }
  } catch (error) {}

  return 0;
}

export function enumHasValue(obj: any, value: string) {
  return Object.values(obj).includes(value);
}

export const removeEmptyFields = (
  data: any,
  removeZeroNumber: boolean = false
): any => {
  if (typeof data !== "object") return data;

  const processedData = Array.isArray(data) ? [...data] : { ...data };
  const fields = Object.keys(data);

  fields.forEach((field) => {
    const value = data[field];
    // Eh texto vazio
    if (typeof value === "string") {
      if (!value.trim()) delete processedData[field];
      // Eh numero e pode remover os zeros
    } else if (typeof value === "number") {
      if (removeZeroNumber && !value) delete processedData[field];
      // Eh boleano
    } else if (typeof value === "boolean") {
      if (value === undefined || value === null) delete processedData[field];
      // Eh uma data
    } else if (value instanceof Date) {
      if (!Boolean(value)) delete processedData[field];
      // Eh um valor ou objeto vazio
    } else if (isEmptyObject(value) || !value) {
      delete processedData[field];
    }
  });

  return processedData;
};

export function formatNumberToMonetary(
  value: number | string,
  withCurrency: boolean = true
): string {
  const currency = lang("currency");
  return withCurrency
    ? `${currency} ${formatNumber(value, 2, 2)}`
    : formatNumber(value, 2, 2);
}

export function formatNumber(
  value: number | string,
  minimumFraction: number = 0,
  maximumFraction: number = 2
): string {
  if (!isNumber(value)) value = 0;
  return Number(value).toLocaleString("pt-BR", {
    minimumFractionDigits: minimumFraction,
    maximumFractionDigits: maximumFraction,
  });
}

export function formatCode(
  num: number | string,
  format: string = "000"
): string {
  const text = String(num);
  if (text.length >= format.length) return text;

  const length = -1 * format.length;
  return (format + text).slice(length);
}

export function keyNameNormalize(name: string): string {
  if (!name) return "";

  return name
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replaceAll(" ", "-");
}

export function initCaptalize(text: string): string {
  if (!text) return text;
  return text[0].toUpperCase() + text.slice(1);
}

export function compareStrings(a: string, b: string): number {
  return String(a).localeCompare(String(b));
}

export function includesText(a: string, b: string): boolean {
  return String(a).toLowerCase().includes(b.toLowerCase());
}

export function copyJson(obj: any) {
  return JSON.parse(JSON.stringify(obj));
}

export function parseTokenJWTToObject(token: string) {
  if (!token || !token.includes(".")) return {};

  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join("")
  );

  return JSON.parse(jsonPayload);
}

export function sleep(ms: number = 100) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function isProductionEnv(): boolean {
  const host = window.location.hostname;

  return (
    process.env.NODE_ENV === "production" &&
    host.indexOf("rubk-faturamento-development") === -1
  );
}

export const rubkNameProduct = isProductionEnv()
  ? "rubk-faturamento"
  : "rubk-faturamento-development";

export const pageDomain = isProductionEnv()
  ? "rubk.app"
  : "mega-rubk-dev.firebaseapp.com";
